/**
 * AIReportPanel.js — Reusable AI report widget.
 *
 * Drop-in component for any page that wants an "AI summary" affordance.
 * Phase 2 of the Admin-AI distribution: instead of routing staff to a
 * centralized chat page, each domain surface (Billing, Patients, CRM, etc.)
 * embeds this panel and gets a one-shot report on demand.
 *
 * Usage:
 *   <AIReportPanel
 *     resource="billing"
 *     title="AI Billing Report"
 *     description="Get an A/R health check and top accounts to call today."
 *   />
 *
 * Props:
 *   resource    — string, dispatch key for routes/ai_reports.js
 *                 (e.g. "billing", "patients", "crm")
 *   title       — heading shown at the top of the panel
 *   description — optional one-liner under the heading
 *   context     — optional object passed to the backend
 *                 (e.g. { patient_id: 42 } for a patient-scoped report)
 *   defaultOpen — boolean (default false). When false, panel starts
 *                 collapsed and the user clicks Generate to expand.
 */
import React, { useState } from 'react';
import { renderMarkdownLite } from './_markdownLite';
import { Sparkles, RefreshCw, ChevronDown, ChevronUp, AlertTriangle, Loader } from 'lucide-react';

export default function AIReportPanel({
  resource,
  title = 'AI Report',
  description = null,
  context = {},
  defaultOpen = false,
}) {
  const [open, setOpen]         = useState(defaultOpen);
  const [loading, setLoading]   = useState(false);
  const [report, setReport]     = useState(null);
  const [error, setError]       = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);

  async function generate() {
    const api = (typeof window !== 'undefined') ? window.zignagenixAPI : null;
    if (!api?.generateAIReport) {
      setError('AI report API not available.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await api.generateAIReport(resource, context);
      if (!r || r.error) {
        setError(r?.message || 'The AI report could not be generated.');
      } else {
        setReport(r.response || '');
        setGeneratedAt(r.generatedAt || new Date().toISOString());
      }
    } catch (err) {
      setError(err?.message || 'Unexpected error generating the report.');
    } finally {
      setLoading(false);
      setOpen(true);
    }
  }

  // ── Markdown-lite rendering ────────────────────────────────────────────
  // ⚠ MOVED to src/components/_markdownLite.js (P182 PR2). It used to live
  // here as a closure whose own comment noted it mirrored a copy in
  // AdminAIPage; the CEO pipeline report would have made a third. Same code,
  // one home.
  const renderMarkdown = renderMarkdownLite;

  return (
    <div className="ai-report-panel">
      <div className="ai-report-header">
        <div className="ai-report-title">
          <Sparkles size={18} />
          <span>{title}</span>
        </div>
        <div className="ai-report-actions">
          {report && !loading && (
            <button
              type="button"
              className="ai-report-icon-btn"
              onClick={generate}
              title="Regenerate"
              aria-label="Regenerate report"
            >
              <RefreshCw size={16} />
            </button>
          )}
          {!report && !loading && (
            <button
              type="button"
              className="ai-report-generate-btn"
              onClick={generate}
              aria-label={`Generate ${title}`}
            >
              <Sparkles size={14} /> Generate
            </button>
          )}
          <button
            type="button"
            className="ai-report-icon-btn"
            onClick={() => setOpen(!open)}
            title={open ? 'Collapse' : 'Expand'}
            aria-label={open ? 'Collapse' : 'Expand'}
            aria-expanded={open}
          >
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {description && open && (
        <div className="ai-report-description">{description}</div>
      )}

      {open && (
        <div className="ai-report-body">
          {loading && (
            <div className="ai-report-loading">
              <Loader size={16} className="ai-report-spin" />
              <span>Generating report — calling Claude via Bedrock…</span>
            </div>
          )}
          {error && (
            <div className="ai-report-error">
              <AlertTriangle size={16} />
              <span>{error}</span>
            </div>
          )}
          {!loading && !error && !report && (
            <p className="ai-report-placeholder">Click <strong>Generate</strong> above to produce a fresh report.</p>
          )}
          {!loading && report && (
            <div className="ai-report-content">{renderMarkdown(report)}</div>
          )}
          {generatedAt && report && !loading && (
            <div className="ai-report-footer">
              Generated {new Date(generatedAt).toLocaleString()} • Powered by AWS Bedrock (BAA-protected)
            </div>
          )}
        </div>
      )}

      <style jsx>{`
        .ai-report-panel {
          background: #FFFFFF;
          border: 1px solid #E8ECF1;
          border-radius: 12px;
          margin-bottom: 16px;
          overflow: hidden;
        }
        .ai-report-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: linear-gradient(135deg, #F0FDFA 0%, #ECFEFF 100%);
          border-bottom: 1px solid #E8ECF1;
        }
        .ai-report-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: #0F766E;
          font-size: 14px;
        }
        .ai-report-actions {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .ai-report-generate-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border: 1px solid #2E8B98;
          background: #2E8B98;
          color: white;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
        }
        .ai-report-generate-btn:hover { background: #256F7A; }
        .ai-report-icon-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          border: 1px solid #E8ECF1;
          background: #FFFFFF;
          color: #6C757D;
          border-radius: 6px;
          cursor: pointer;
        }
        .ai-report-icon-btn:hover { background: #F1F5F9; color: #2E8B98; }
        .ai-report-description {
          padding: 8px 16px 0;
          color: #6C757D;
          font-size: 12px;
        }
        .ai-report-body { padding: 16px; }
        .ai-report-loading {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #6C757D;
          font-size: 14px;
        }
        .ai-report-spin {
          animation: ai-report-rotate 1s linear infinite;
        }
        @keyframes ai-report-rotate {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .ai-report-error {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #B91C1C;
          background: #FEE2E2;
          padding: 10px 12px;
          border-radius: 6px;
          font-size: 13px;
        }
        .ai-report-placeholder {
          color: #6C757D;
          font-size: 13px;
          margin: 0;
        }
        .ai-report-content {
          color: #1f2937;
          font-size: 14px;
          line-height: 1.55;
        }
        .ai-report-footer {
          margin-top: 12px;
          padding-top: 8px;
          border-top: 1px solid #E8ECF1;
          color: #9CA3AF;
          font-size: 11px;
        }
      `}</style>
    </div>
  );
}
